/**
 * AppContext.jsx
 * Global state provider for the GlobalOps platform.
 *
 * Architecture: React Context API (chosen over Redux for simplicity — single
 * data layer with ~15 CRUD functions, no complex middleware needed).
 *
 * Data flow:
 *   1. fetchAll() loads all entities from the PostgreSQL API on mount
 *   2. A mutation puts the server's answer straight into the lists and
 *      re-reads only the lists it can have touched, quietly (refreshQuietly).
 *      A project save no longer waits for that re-read: it used to await a
 *      full fetchAll - users, accounts, 3 MB of projects and 24 MB of engineer
 *      photos - before the editor could say "saved" (Sharif 10/13/26: "Save
 *      sometimes didn't save")
 *   3. Year-filtered memos (opportunitiesThisYear, projectsThisYear) derive from raw data
 *   4. pipelineStats is a computed memo from opportunities + projects
 *   5. Other people's changes arrive over the live stream (src/live) and
 *      re-read just the lists they touched - see liveTargets below
 *   6. myAccess is the signed-in person's effective access (GET
 *      /api/users/me: their role plus their own overrides). It is re-read on
 *      every 'users' change, so tabs and buttons follow an access change
 *      without a reload (hooks/useMyAccess reads it)
 *
 * Exported via useApp() hook — must be used inside <AppProvider>.
 *
 * This module's path and exports are the contract (many tests mock it); the
 * internals live in ./app/: apiClient and rows (module helpers), and the
 * hooks AppProvider calls in order - useLiveLists, useYearData,
 * useAccountActions, useProjectActions, useEngineerActions.
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getRole } from '../data/admins';
import { listsFor, getMyAccess, readers, getList } from './app/apiClient';
import { useLiveLists } from './app/useLiveLists';
import { useYearData } from './app/useYearData';
import { useAccountActions } from './app/useAccountActions';
import { useProjectActions } from './app/useProjectActions';
import { useEngineerActions } from './app/useEngineerActions';
export { listedProject, withSavedRow, toCamel, toSnake, quoteDateLocal, quoteYear } from './app/rows';
export { liveTargets } from './app/apiClient';

export const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [allAccounts, setAllAccounts] = useState([]);
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allEngineers, setAllEngineers] = useState([]);
  const [users, setUsers] = useState([]);
  const [myAccess, setMyAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ── Year filter ── */
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  // Compare mode (Dashboard only): when non-null, dashboard renders side-by-side
  // columns, one per year. Initialized from the URL hash (?compare=2025,2026)
  // so the comparison survives a page refresh / shared link.
  const [compareYears, setCompareYearsRaw] = useState(() => {
    try {
      const hash = window.location.hash;
      const qIdx = hash.indexOf('?');
      if (qIdx < 0) return null;
      const params = new URLSearchParams(hash.slice(qIdx + 1));
      const raw = params.get('compare');
      if (!raw) return null;
      const years = raw
        .split(',')
        .map((y) => parseInt(y, 10))
        .filter(Boolean);
      return years.length >= 2 ? [...years].sort() : null;
    } catch {
      return null;
    }
  });
  // Wrap setter to also write back to the URL hash so refresh keeps the mode.
  const setCompareYears = useCallback((next) => {
    setCompareYearsRaw(next);
    try {
      const hash = window.location.hash;
      const qIdx = hash.indexOf('?');
      const base = qIdx < 0 ? hash : hash.slice(0, qIdx);
      const params = new URLSearchParams(qIdx < 0 ? '' : hash.slice(qIdx + 1));
      if (next && next.length >= 2) params.set('compare', next.join(','));
      else params.delete('compare');
      const qs = params.toString();
      const newHash = qs ? `${base}?${qs}` : base;
      if (window.location.hash !== newHash) {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${newHash}`);
      }
    } catch {
      /* hash mutation blocked — ignore */
    }
  }, []);

  // each list's reads (readers above) and the roster the live re-reads work
  // out the caller's role from
  const [gens] = useState(readers);
  const usersRef = useRef(users);
  useEffect(() => {
    usersRef.current = users;
  }, [users]);
  const myAccessRef = useRef(null);
  // a different person signed in: the last one's access is not theirs
  const myEmail = user?.email || '';
  useEffect(() => {
    myAccessRef.current = null;
    setMyAccess(null);
  }, [myEmail]);
  // what decides the lists: the server's answer, else the role from the roster
  const accessNow = useCallback(() => myAccessRef.current || getRole(user?.email, usersRef.current), [user]);

  /* ── Fetch all data ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch the roster first so we know the caller's role, then request ONLY
      // the endpoints this role can read — the API gate 403s the rest (engineers
      // and crm-staff have no BD/financial access, etc.). Skipping avoids 403
      // noise and empty-data flicker. A roster that cannot be read leaves the
      // one already known in place.
      const tUsers = gens.users.start();
      const [usersData, mine] = await Promise.all([getList('/api/users'), getMyAccess()]);
      if (usersData && gens.users.show(tUsers)) {
        usersRef.current = usersData;
        setUsers(usersData);
      }
      if (mine) {
        myAccessRef.current = mine;
        setMyAccess(mine);
      }
      const want = listsFor(accessNow());
      if (!want.projects && !want.accounts && !want.engineers) {
        // engineer / crm-staff / pending — no BD data to load here (and a read
        // still out from before must not put rows back)
        for (const key of ['accounts', 'projects', 'engineers']) gens[key].show(gens[key].start());
        setAllAccounts([]);
        setAllProjects([]);
        setAllOpportunities([]);
        setAllEngineers([]);
        return;
      }
      const t = { accounts: gens.accounts.start(), projects: gens.projects.start(), engineers: gens.engineers.start() };
      const [accts, projs, engs] = await Promise.all([
        want.accounts ? getList('/api/accounts') : Promise.resolve([]),
        want.projects ? getList('/api/projects') : Promise.resolve([]),
        want.engineers ? getList('/api/engineers') : Promise.resolve([]),
      ]);
      if (accts && gens.accounts.show(t.accounts)) setAllAccounts(accts);
      if (projs && gens.projects.show(t.projects)) {
        setAllProjects(projs);
        setAllOpportunities(projs);
      }
      if (engs && gens.engineers.show(t.engineers)) setAllEngineers(engs);
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [gens, accessNow]);

  // Only fetch once someone is signed in — the API requires auth, and the
  // login screen has no business firing data requests (it just 401-spams).
  useEffect(() => {
    if (user) fetchAll();
  }, [fetchAll, user]);

  // My own access failed to load (a deploy restarting the API answers 502):
  // without a retry the tab stayed on the role defaults until a reload, hiding
  // anything an override or the bootstrap admin's extras open (10/7/26).
  // ponytail: fixed 5 s steps, 12 tries (a minute) - a restart takes seconds.
  useEffect(() => {
    if (!user || myAccess) return undefined;
    let tries = 0;
    let timer;
    const tick = async () => {
      const mine = await getMyAccess();
      if (mine) {
        myAccessRef.current = mine;
        setMyAccess(mine);
        fetchAll();
        return;
      }
      if (++tries < 12) timer = setTimeout(tick, 5000);
    };
    timer = setTimeout(tick, 5000);
    return () => clearTimeout(timer);
  }, [user, myAccess, fetchAll]);

  const refresh = useCallback(() => fetchAll(), [fetchAll]);

  const { refreshQuietly, dropProject, settleProject, applySavedProject } = useLiveLists({
    user,
    gens,
    usersRef,
    myAccessRef,
    setUsers,
    setMyAccess,
    fetchAll,
    accessNow,
    setAllAccounts,
    setAllProjects,
    setAllOpportunities,
    setAllEngineers,
  });

  const { opportunitiesThisYear, projectsThisYear, pipelineStats } = useYearData({
    selectedYear,
    allProjects,
    allOpportunities,
  });

  /* ── Recently Viewed (localStorage) ── */
  const trackRecentlyViewed = useCallback((type, id, label) => {
    try {
      const key = 'globalops_recent';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const filtered = existing.filter((r) => !(r.type === type && r.id === id));
      const entry = { type, id, label, ts: Date.now() };
      const updated = [entry, ...filtered].slice(0, 5);
      localStorage.setItem(key, JSON.stringify(updated));
    } catch (e) {
      /* ignore */
    }
  }, []);

  const { addAccount, updateAccount, deleteAccount, deleteOpportunity } = useAccountActions({ refreshQuietly, dropProject });

  const { addProject, updateProject, createProject, saveProject, deleteProject } = useProjectActions({
    settleProject,
    dropProject,
    refreshQuietly,
  });

  const { resolveOwnerName, updateUserRole, addEngineer, updateEngineer, deleteEngineer } = useEngineerActions({
    refreshQuietly,
    fetchAll,
    users,
  });

  return (
    <AppContext.Provider
      value={{
        loading,
        allAccounts,
        allOpportunities,
        allProjects,
        allEngineers,
        users,
        resolveOwnerName,
        updateUserRole,
        myAccess,
        opportunitiesThisYear,
        projectsThisYear,
        selectedYear,
        setSelectedYear,
        compareYears,
        setCompareYears,
        pipelineStats,
        refresh,
        fetchAll,
        refreshQuietly,
        applySavedProject,
        addAccount,
        addProject,
        addEngineer,
        updateAccount,
        updateProject,
        updateEngineer,
        createProject,
        saveProject,
        deleteAccount,
        deleteOpportunity,
        deleteProject,
        deleteEngineer,
        trackRecentlyViewed,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
